import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { EmailInput } from '@/components/email-input';
import { KeyboardAwareScrollView } from '@/components/with-keyboard-aware';
import { PadBusinessTypeSelector } from './pad-business-type-selector';
import {
  PadFormState,
  PadPaymentFormAction,
  PadPaymentFormActions,
} from '@/hooks/payments/use-pad-payment-form-reducer';
import { TextInput } from '@/components/text-input';
import { TextStyle, View } from 'react-native';

interface Props {
  dispatch: React.Dispatch<PadPaymentFormAction>;
  formState: PadFormState;
  isCollectingConsent?: boolean;
}

export const CollectPADConsentForm: React.FC<Props> = ({
  dispatch,
  formState,
}) => {
  const [styles, theme] = useStyles();
  const { contactInfo } = formState;

  const updateContactField = (
    field: keyof PadFormState['contactInfo'],
    value: string,
  ) => {
    dispatch({
      payload: { field, value },
      type: PadPaymentFormActions.SET_CONTACT_FIELD,
    });
  };

  return (
    <>
      <KeyboardAwareScrollView
        keyboardDismissMode="on-drag"
        style={styles.contentContainer}
      >
        <View style={styles.inputFieldsContainer}>
          <View style={styles.fieldContainer}>
            <TextInput
              containerStyle={styles.inputFieldContainer}
              focusedBorderColor={theme.sk.color.border.input.active}
              label={i18n.t('pad.account_owner_name')}
              labelStyle={theme.sk.typography.label.md as TextStyle}
              onChangeText={(value) => updateContactField('payeeName', value)}
              selectionColor={theme.sk.color.border.input.active}
              useOuterLabel
              value={contactInfo.payeeName}
            />
          </View>
          <View style={styles.fieldContainer}>
            <TextInput
              containerStyle={styles.inputFieldContainer}
              focusedBorderColor={theme.sk.color.border.input.active}
              keyboardType="phone-pad"
              label={i18n.t('pad.phone')}
              labelStyle={theme.sk.typography.label.md as TextStyle}
              onChangeText={(value) => updateContactField('phoneNumber', value)}
              selectionColor={theme.sk.color.border.input.active}
              useOuterLabel
              value={contactInfo.phoneNumber}
            />
          </View>
          <View style={styles.fieldContainer}>
            <EmailInput
              containerStyle={styles.inputFieldContainer}
              focusedBorderColor={theme.sk.color.border.input.active}
              label={i18n.t('pad.email')}
              labelStyle={theme.sk.typography.label.md as TextStyle}
              onChangeText={(value) => updateContactField('email', value)}
              placeholder=""
              selectionColor={theme.sk.color.border.input.active}
              useOuterLabel
              value={contactInfo.email}
            />
          </View>
          <View style={styles.fieldContainer}>
            <TextInput
              containerStyle={styles.inputFieldContainer}
              focusedBorderColor={theme.sk.color.border.input.active}
              label={i18n.t('pad.street')}
              labelStyle={theme.sk.typography.label.md as TextStyle}
              onChangeText={(value) => updateContactField('street', value)}
              selectionColor={theme.sk.color.border.input.active}
              useOuterLabel
              value={contactInfo.street}
            />
          </View>
          <View style={styles.fieldContainer}>
            <TextInput
              containerStyle={styles.inputFieldContainer}
              focusedBorderColor={theme.sk.color.border.input.active}
              label={i18n.t('pad.city')}
              labelStyle={theme.sk.typography.label.md as TextStyle}
              onChangeText={(value) => updateContactField('city', value)}
              selectionColor={theme.sk.color.border.input.active}
              useOuterLabel
              value={contactInfo.city}
            />
          </View>
          <View style={styles.row}>
            <View style={[styles.fieldContainer, styles.halfWidth]}>
              <TextInput
                containerStyle={styles.inputFieldContainer}
                focusedBorderColor={theme.sk.color.border.input.active}
                label={i18n.t('pad.province')}
                labelStyle={theme.sk.typography.label.md as TextStyle}
                onChangeText={(value) => updateContactField('province', value)}
                selectionColor={theme.sk.color.border.input.active}
                useOuterLabel
                value={contactInfo.province}
              />
            </View>
            <View style={[styles.fieldContainer, styles.halfWidth]}>
              <TextInput
                containerStyle={styles.inputFieldContainer}
                focusedBorderColor={theme.sk.color.border.input.active}
                label={i18n.t('pad.postal_code')}
                labelStyle={theme.sk.typography.label.md as TextStyle}
                onChangeText={(value) =>
                  updateContactField('postalCode', value)
                }
                selectionColor={theme.sk.color.border.input.active}
                useOuterLabel
                value={contactInfo.postalCode}
              />
            </View>
          </View>
          <PadBusinessTypeSelector
            onChange={(type) => {
              dispatch({
                payload: { padBusinessType: type },
                type: PadPaymentFormActions.SET_BUSINESS_TYPE,
              });
            }}
            padBusinessType={formState.padBusinessType}
          />
        </View>
      </KeyboardAwareScrollView>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  contentContainer: {
    paddingHorizontal: 16,
  },
  fieldContainer: {
    marginBottom: 16,
  },
  halfWidth: {
    flex: 1,
  },
  inputFieldContainer: {
    borderColor: theme.sk.color.border.input.default,
    borderRadius: 8,
    borderWidth: 1,
  },
  inputFieldsContainer: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
}));
